import React, { useState } from 'react';
import { ACCENT, ACCENT_LIGHT, TEXT_DARK, TEXT_MID, TEXT_LIGHT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import {
  Card,
  Field,
  ScreenWrap,
  SectionLabel,
  SubmitBtn,
  Tag,
} from '../components/ui/Primitives';
import { useRealtimeTable } from '../lib/useRealtimeTable';
import { supabase, hasSupabase } from '../lib/supabase';
import { useSchedule } from '../lib/useSchedule';
import { useBreakpoint } from '../lib/useBreakpoint';

const FALLBACK = [
  {
    id: 'demo-1',
    name: 'Sarah Mitchell',
    idea: 'Create a shared AI prompt library accessible to all state EDs throughout the year — not just at events.',
    session_tag: 'S1',
    created_at: '2026-05-21T09:48:00-07:00',
  },
  {
    id: 'demo-2',
    name: 'James Okafor',
    idea: 'Develop a standardized succession planning template we can all adapt for our individual state bylaws.',
    session_tag: 'S1',
    created_at: '2026-05-21T09:31:00-07:00',
  },
  {
    id: 'demo-3',
    name: 'Linda Torres',
    idea: 'Explore a consortium model for bulk-purchasing continuing education content across state associations.',
    session_tag: 'S0',
    created_at: '2026-05-21T08:52:00-07:00',
  },
];

export default function ParkingLotScreen() {
  const { rows, setRows } = useRealtimeTable('parking_lot');
  const ideas = rows.length ? rows : hasSupabase ? [] : FALLBACK;

  const [name, setName] = useState('');
  const [idea, setIdea] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { current } = useSchedule();

  const submit = async () => {
    if (!name.trim() || !idea.trim()) return;
    const payload = {
      name: name.trim(),
      idea: idea.trim(),
      session_tag: current ? abbreviate(current.num) : 'Live',
    };
    setName('');
    setIdea('');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2500);

    if (hasSupabase) {
      // Don't optimistically prepend — the realtime INSERT channel fans out the
      // canonical row within ~200ms and prepending here would cause it to
      // briefly show twice.
      await supabase.from('parking_lot').insert(payload);
    } else {
      // Offline-only fallback (no Supabase configured).
      setRows((prev) => [
        { id: `local-${Date.now()}`, ...payload, created_at: new Date().toISOString() },
        ...prev,
      ]);
    }
  };

  const visible = ideas.filter((i) => !i.hidden);
  const bp = useBreakpoint();

  const submitCard = (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: TEXT_DARK,
          letterSpacing: '-0.01em',
          marginBottom: 2,
        }}
      >
        💡 Share an Idea
      </div>
      <Field label="Your Name" value={name} onChange={setName} placeholder="e.g. Sarah Mitchell" />
      <Field
        label="Your Idea"
        value={idea}
        onChange={setIdea}
        placeholder="What's on your mind?"
        multiline
      />
      {submitted ? (
        <div
          style={{
            background: '#dcfce7',
            border: '1px solid #86efac',
            borderRadius: 9,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Ic.Check size={16} color="#16a34a" />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>
            Idea added to the board!
          </span>
        </div>
      ) : (
        <SubmitBtn onClick={submit} disabled={!name.trim() || !idea.trim()}>
          Submit Idea
        </SubmitBtn>
      )}
    </Card>
  );

  const feed = (
    <>
      <SectionLabel>
        IDEA BOARD · {visible.length} {visible.length === 1 ? 'IDEA' : 'IDEAS'}
      </SectionLabel>
      {visible.map((item) => (
        <Card key={item.id} style={{ padding: '13px 14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 7,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: ACCENT_LIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>
                  {item.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK, lineHeight: 1.2 }}>
                  {item.name}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: TEXT_LIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 1,
                  }}
                >
                  <Ic.Clock size={10} color={TEXT_LIGHT} /> {formatTime(item.created_at)}
                </div>
              </div>
            </div>
            <Tag>{item.session_tag || 'Live'}</Tag>
          </div>
          <p style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.55, margin: 0 }}>{item.idea}</p>
        </Card>
      ))}
    </>
  );

  // ── Desktop: form on left (sticky), live feed on right (scrollable) ────
  if (bp === 'desktop') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {submitCard}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{feed}</div>
      </div>
    );
  }

  // ── Mobile / tablet: original single-column ────────────────────────────
  return (
    <ScreenWrap>
      {submitCard}
      {feed}
    </ScreenWrap>
  );
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function abbreviate(num) {
  if (num === 'Closing') return 'CL';
  return num.replace('Session ', 'S');
}
